import type { FeedbackCategory } from '../index.js'

export interface FeedbackCategoryOption {
  category: FeedbackCategory
  label: string
  /** One line under the label, so the person picks the right one without guessing. */
  description: string
  /** A prompt for the comment box, in the terms of that category. */
  placeholder: string
}

/** The categories as the form offers them, in display order; the first is the default. Mirrors the API's `FeedbackCategory`. */
export const FEEDBACK_CATEGORY_OPTIONS: readonly FeedbackCategoryOption[] = [
  { category: 'general', label: 'General comment', description: 'Anything at all - an impression, a thank-you, a thought.', placeholder: 'What is on your mind?' },
  { category: 'feature_request', label: 'Feature request', description: 'Something you want the product to do that it does not yet.', placeholder: 'What should it do, and what would that let you do?' },
  { category: 'bug', label: 'Bug', description: 'Something is broken or behaves wrongly.', placeholder: 'What did you do, what happened, and what did you expect?' },
  { category: 'usability', label: 'Usability', description: 'It works, but it is confusing, hard to find or slow to use.', placeholder: 'What got in the way?' },
  { category: 'data_issue', label: 'Data issue', description: 'A number, score, tier or record looks wrong.', placeholder: 'Which record, what does it show, and what should it show?' },
  { category: 'performance', label: 'Performance', description: 'Slow to load or slow to respond.', placeholder: 'Which screen or action, and how long did it take?' },
  { category: 'question', label: 'Question', description: 'You are unsure how something works or what it means.', placeholder: 'What would you like explained?' },
  { category: 'security', label: 'Security or privacy', description: 'Something that could expose data, or let someone see what they should not.', placeholder: 'What did you see, and who could see it?' },
]

export const DEFAULT_FEEDBACK_CATEGORY: FeedbackCategory = 'general'

const BY_KEY = new Map(FEEDBACK_CATEGORY_OPTIONS.map((option) => [option.category, option]))

/** The option for a category; an unknown one (an API newer than this app) reads as general. */
export function feedbackCategoryOption(category: FeedbackCategory | string): FeedbackCategoryOption {
  return BY_KEY.get(category as FeedbackCategory) ?? FEEDBACK_CATEGORY_OPTIONS[0]!
}

/** The badge colour per category: coral for things that are wrong, marine for everything else, so a bug stands out in a list. */
export const FEEDBACK_CATEGORY_CLASS: Record<FeedbackCategory, string> = {
  general: 'bg-silver-lt text-ink-mid border-silver/60',
  feature_request: 'bg-marine-lt text-marine-dk border-marine/30',
  bug: 'bg-coral-lt text-coral border-coral/50',
  usability: 'bg-marine-lt text-marine-dk border-marine/30',
  data_issue: 'bg-coral-lt text-coral border-coral/50',
  performance: 'bg-gold-lt text-gold border-gold/50',
  question: 'bg-marine-lt text-marine-dk border-marine/30',
  security: 'bg-coral-lt text-coral border-coral/50',
}
